/**
 * Servicio centralizado para manejo de estados de tickets
 * Portal de Soporte Ecomac - Sistema de Estados Workflow
 */

export interface TicketStatus {
  key: string;
  internalName: string;
  displayName: string;
  category: 'no_iniciado' | 'activo' | 'hecho' | 'cerrado';
  color: {
    background: string;
    text: string;
    border: string;
    badge: string;
  };
  icon: string;
  description: string;
  order: number;
}

export interface StatusCategory {
  key: string;
  name: string;
  description: string;
  color: string;
  statuses: string[];
}

/**
 * Configuración completa de estados de tickets Ecomac
 */
export class TicketStatusService {
  
  /**
   * Mapeo completo de estados del sistema
   */
  private static readonly STATUS_MAP: Record<string, TicketStatus> = {
    // === NO INICIADO ===
    PENDIENTES: {
      key: 'PENDIENTES',
      internalName: 'PENDIENTES',
      displayName: 'Pendiente',
      category: 'no_iniciado',
      color: {
        background: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-300',
        badge: 'bg-gray-500'
      },
      icon: '⏳',
      description: 'Ticket pendiente de inicio',
      order: 1
    },

    // === ACTIVO ===
    EVALUACION: {
      key: 'EVALUACION',
      internalName: 'EVALUACIÓN',
      displayName: 'En análisis',
      category: 'activo',
      color: {
        background: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-300',
        badge: 'bg-blue-500'
      },
      icon: '🔍',
      description: 'Ticket en proceso de evaluación',
      order: 2
    },

    PENDIENTE_DEV: {
      key: 'PENDIENTE_DEV',
      internalName: 'PENDIENTE DEV',
      displayName: 'Derivado a Desarrollo',
      category: 'activo',
      color: {
        background: 'bg-indigo-100',
        text: 'text-indigo-800',
        border: 'border-indigo-300',
        badge: 'bg-indigo-500'
      },
      icon: '📋',
      description: 'Ticket asignado al equipo de desarrollo',
      order: 3
    },

    EN_DESARROLLO: {
      key: 'EN_DESARROLLO',
      internalName: 'EN DESARROLLO',
      displayName: 'En proceso',
      category: 'activo',
      color: {
        background: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-300',
        badge: 'bg-purple-500'
      },
      icon: '⚙️',
      description: 'Ticket siendo desarrollado activamente',
      order: 4
    },

    TESTEO_LOCAL: {
      key: 'TESTEO_LOCAL',
      internalName: 'TESTEO LOCAL',
      displayName: 'Testeo en desarrollo',
      category: 'activo',
      color: {
        background: 'bg-cyan-100',
        text: 'text-cyan-800',
        border: 'border-cyan-300',
        badge: 'bg-cyan-500'
      },
      icon: '🧪',
      description: 'Ticket en fase de testing local',
      order: 5
    },

    PENDIENTE_SERVIDOR_DEV: {
      key: 'PENDIENTE_SERVIDOR_DEV',
      internalName: 'PENDIENTE SERVIDOR DEV',
      displayName: 'Implementando en desarrollo',
      category: 'activo',
      color: {
        background: 'bg-teal-100',
        text: 'text-teal-800',
        border: 'border-teal-300',
        badge: 'bg-teal-500'
      },
      icon: '🚀',
      description: 'Ticket esperando deployment en servidor de desarrollo',
      order: 6
    },

    TESTEO_SERVIDOR_DEV: {
      key: 'TESTEO_SERVIDOR_DEV',
      internalName: 'TESTEO SERVIDOR DEV',
      displayName: 'Testeando en desarrollo',
      category: 'activo',
      color: {
        background: 'bg-emerald-100',
        text: 'text-emerald-800',
        border: 'border-emerald-300',
        badge: 'bg-emerald-500'
      },
      icon: '🔬',
      description: 'Ticket siendo testeado en servidor de desarrollo',
      order: 7
    },

    PENDIENTE_A_QA: {
      key: 'PENDIENTE_A_QA',
      internalName: 'PENDIENTE A QA',
      displayName: 'Enviado a QA',
      category: 'activo',
      color: {
        background: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-300',
        badge: 'bg-orange-500'
      },
      icon: '📤',
      description: 'Ticket enviado al equipo de QA',
      order: 8
    },

    TESTEO_SERVIDOR_QA: {
      key: 'TESTEO_SERVIDOR_QA',
      internalName: 'TESTEO SERVIDOR QA',
      displayName: 'Testeando en QA',
      category: 'activo',
      color: {
        background: 'bg-amber-100',
        text: 'text-amber-800',
        border: 'border-amber-300',
        badge: 'bg-amber-500'
      },
      icon: '🔍',
      description: 'Ticket siendo validado por QA',
      order: 9
    },

    BUGS_ENCONTRADOS: {
      key: 'BUGS_ENCONTRADOS',
      internalName: 'BUGS ENCONTRADOS',
      displayName: 'Bug encontrado',
      category: 'activo',
      color: {
        background: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-300',
        badge: 'bg-red-500'
      },
      icon: '🐛',
      description: 'Se encontraron bugs que requieren corrección',
      order: 10
    },

    // === HECHO ===
    BUGS_CORREGIDOS: {
      key: 'BUGS_CORREGIDOS',
      internalName: 'BUGS CORREGIDOS',
      displayName: 'Bug Corregido',
      category: 'hecho',
      color: {
        background: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300',
        badge: 'bg-green-500'
      },
      icon: '✅',
      description: 'Bugs corregidos, listo para cierre',
      order: 11
    },

    // === CERRADO ===
    LISTO: {
      key: 'LISTO',
      internalName: 'LISTO',
      displayName: 'Cerrado',
      category: 'cerrado',
      color: {
        background: 'bg-slate-100',
        text: 'text-slate-800',
        border: 'border-slate-300',
        badge: 'bg-slate-500'
      },
      icon: '🎯',
      description: 'Ticket completado y cerrado',
      order: 12
    }
  };

  /**
   * Categorías de estados para agrupación
   */
  private static readonly CATEGORIES: Record<string, StatusCategory> = {
    no_iniciado: {
      key: 'no_iniciado',
      name: 'No Iniciado',
      description: 'Tickets que aún no han comenzado',
      color: 'bg-gray-500',
      statuses: ['PENDIENTES']
    },
    activo: {
      key: 'activo',
      name: 'En Proceso',
      description: 'Tickets siendo trabajados activamente',
      color: 'bg-blue-500',
      statuses: [
        'EVALUACION', 'PENDIENTE_DEV', 'EN_DESARROLLO', 'TESTEO_LOCAL',
        'PENDIENTE_SERVIDOR_DEV', 'TESTEO_SERVIDOR_DEV', 'PENDIENTE_A_QA',
        'TESTEO_SERVIDOR_QA', 'BUGS_ENCONTRADOS'
      ]
    },
    hecho: {
      key: 'hecho',
      name: 'Completado',
      description: 'Tickets completados listos para cierre',
      color: 'bg-green-500',
      statuses: ['BUGS_CORREGIDOS']
    },
    cerrado: {
      key: 'cerrado',
      name: 'Cerrado',
      description: 'Tickets finalizados',
      color: 'bg-slate-500',
      statuses: ['LISTO']
    }
  };

  /**
   * Obtener información completa de un estado
   */
  static getStatusInfo(statusKey: string): TicketStatus | null {
    // Normalizar el key (remover espacios, convertir a mayúsculas, etc.)
    const normalizedKey = this.normalizeStatusKey(statusKey);
    return this.STATUS_MAP[normalizedKey] || null;
  }

  /**
   * Obtener todos los estados disponibles
   */
  static getAllStatuses(): TicketStatus[] {
    return Object.values(this.STATUS_MAP).sort((a, b) => a.order - b.order);
  }

  /**
   * Obtener estados por categoría
   */
  static getStatusesByCategory(category: string): TicketStatus[] {
    return this.getAllStatuses().filter(status => status.category === category);
  }

  /**
   * Obtener información de una categoría
   */
  static getCategoryInfo(categoryKey: string): StatusCategory | null {
    return this.CATEGORIES[categoryKey] || null;
  }

  /**
   * Obtener todas las categorías
   */
  static getAllCategories(): StatusCategory[] {
    return Object.values(this.CATEGORIES);
  }

  /**
   * Normalizar key de estado para búsqueda
   */
  private static normalizeStatusKey(statusKey: string): string {
    if (!statusKey) return '';
    
    return statusKey
      .toString()
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[ÁÀÄ]/g, 'A')
      .replace(/[ÉÈË]/g, 'E') 
      .replace(/[ÍÌÏ]/g, 'I')
      .replace(/[ÓÒÖ]/g, 'O')
      .replace(/[ÚÙÜ]/g, 'U')
      .replace(/Ñ/g, 'N');
  }

  /**
   * Obtener el siguiente estado en el workflow
   */
  static getNextStatus(currentStatus: string): TicketStatus | null {
    const current = this.getStatusInfo(currentStatus);
    if (!current) return null;

    const allStatuses = this.getAllStatuses();
    const currentIndex = allStatuses.findIndex(s => s.key === current.key);
    
    if (currentIndex === -1 || currentIndex === allStatuses.length - 1) {
      return null; // Es el último estado
    }

    return allStatuses[currentIndex + 1];
  }

  /**
   * Obtener el estado anterior en el workflow
   */
  static getPreviousStatus(currentStatus: string): TicketStatus | null {
    const current = this.getStatusInfo(currentStatus);
    if (!current) return null;

    const allStatuses = this.getAllStatuses();
    const currentIndex = allStatuses.findIndex(s => s.key === current.key);
    
    if (currentIndex <= 0) {
      return null; // Es el primer estado
    }

    return allStatuses[currentIndex - 1];
  }

  /**
   * Verificar si un estado es válido
   */
  static isValidStatus(statusKey: string): boolean {
    return this.getStatusInfo(statusKey) !== null;
  }

  /**
   * Obtener estados válidos para transición desde un estado actual
   */
  static getValidTransitions(currentStatus: string): TicketStatus[] {
    const current = this.getStatusInfo(currentStatus);
    if (!current) return [];

    const allStatuses = this.getAllStatuses();
    const currentIndex = allStatuses.findIndex(s => s.key === current.key);
    
    // Permitir avanzar al siguiente estado o retroceder al anterior
    const validTransitions: TicketStatus[] = [];
    
    // Puede avanzar al siguiente
    if (currentIndex < allStatuses.length - 1) {
      validTransitions.push(allStatuses[currentIndex + 1]);
    }
    
    // Puede retroceder al anterior (solo en desarrollo)
    if (currentIndex > 0 && current.category === 'activo') {
      validTransitions.push(allStatuses[currentIndex - 1]);
    }
    
    // Casos especiales para bugs
    if (current.key === 'TESTEO_SERVIDOR_QA') {
      // Desde QA puede ir a bug encontrado
      const bugStatus = this.getStatusInfo('BUGS_ENCONTRADOS');
      if (bugStatus) validTransitions.push(bugStatus);
    }
    
    if (current.key === 'BUGS_ENCONTRADOS') {
      // Desde bug encontrado puede volver a desarrollo
      const devStatus = this.getStatusInfo('EN_DESARROLLO');
      if (devStatus) validTransitions.push(devStatus);
    }
    
    return validTransitions;
  }

  /**
   * Calcular progreso del ticket (0-100%)
   */
  static calculateProgress(statusKey: string): number {
    const status = this.getStatusInfo(statusKey);
    if (!status) return 0;

    const totalStates = this.getAllStatuses().length;
    const progressPercentage = Math.round((status.order / totalStates) * 100);
    
    return Math.min(100, Math.max(0, progressPercentage));
  }

  /**
   * Obtener estadísticas por categoría y estados simples
   */
  static getStatusStatistics(tickets: any[]): Record<string, number> {
    const stats: Record<string, number> = {
      // Contadores por categoría
      no_iniciado: 0,
      activo: 0,
      hecho: 0,
      cerrado: 0,
      // Contadores por estado simple (para compatibilidad)
      abierto: 0,
      en_progreso: 0,
      resuelto: 0,
      // cerrado ya está arriba
    };

    // Contar tickets por estado simple Y categoría
    tickets.forEach(ticket => {
      const ticketStatus = ticket.status;
      
      // Contadores directos por estado simple
      if (stats.hasOwnProperty(ticketStatus)) {
        stats[ticketStatus]++;
      }
      
      // Mapeo a categorías para el workflow avanzado
      const mappedStatus = this.mapClickUpStatus(ticketStatus);
      const statusInfo = this.getStatusInfo(mappedStatus);
      
      if (statusInfo) {
        stats[statusInfo.category]++;
      } else {
        // Fallback para estados simples que no están en el workflow
        switch (ticketStatus) {
          case 'abierto':
            stats.no_iniciado++;
            break;
          case 'en_progreso':
            stats.activo++;
            break;
          case 'resuelto':
            stats.hecho++;
            break;
          case 'cerrado':
            stats.cerrado++;
            break;
        }
      }
    });

    return stats;
  }

  /**
   * Obtener color de progreso según el estado
   */
  static getProgressColor(statusKey: string): string {
    const status = this.getStatusInfo(statusKey);
    if (!status) return 'bg-gray-400';

    switch (status.category) {
      case 'no_iniciado':
        return 'bg-gray-400';
      case 'activo':
        return 'bg-blue-500';
      case 'hecho':
        return 'bg-green-500';
      case 'cerrado':
        return 'bg-slate-500';
      default:
        return 'bg-gray-400';
    }
  }

  /**
   * Formatear estado para display en UI
   */
  static formatStatusForDisplay(statusKey: string, includeIcon: boolean = true): string {
    const status = this.getStatusInfo(statusKey);
    if (!status) return statusKey;

    return includeIcon 
      ? `${status.icon} ${status.displayName}`
      : status.displayName;
  }

  /**
   * Obtener clase CSS completa para un estado
   */
  static getStatusClasses(statusKey: string): string {
    const status = this.getStatusInfo(statusKey);
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-300';

    return `${status.color.background} ${status.color.text} ${status.color.border}`;
  }

  /**
   * Mapear estado de ClickUp a estado interno
   */
  static mapClickUpStatus(clickupStatus: string): string {
    if (!clickupStatus) return 'PENDIENTES';
    
    // Normalizar entrada
    const normalizedInput = clickupStatus.toString().toUpperCase().trim();
    
    // Mapeo directo de estados de ClickUp workflow completo
    const clickupMap: Record<string, string> = {
      // Estados del workflow completo
      'PENDIENTES': 'PENDIENTES',
      'EVALUACIÓN': 'EVALUACION', 
      'EVALUACION': 'EVALUACION',
      'PENDIENTE DEV': 'PENDIENTE_DEV',
      'EN DESARROLLO': 'EN_DESARROLLO',
      'TESTEO LOCAL': 'TESTEO_LOCAL',
      'PENDIENTE SERVIDOR DEV': 'PENDIENTE_SERVIDOR_DEV',
      'TESTEO SERVIDOR DEV': 'TESTEO_SERVIDOR_DEV',
      'PENDIENTE A QA': 'PENDIENTE_A_QA',
      'TESTEO SERVIDOR QA': 'TESTEO_SERVIDOR_QA',
      'BUGS ENCONTRADOS': 'BUGS_ENCONTRADOS',
      'BUGS CORREGIDOS': 'BUGS_CORREGIDOS',
      'LISTO': 'LISTO',
      
      // Mapeo de estados simplificados (portal cliente)
      'ABIERTO': 'PENDIENTES',
      'EN_PROGRESO': 'EN_DESARROLLO',
      'EN PROGRESO': 'EN_DESARROLLO', 
      'RESUELTO': 'BUGS_CORREGIDOS',
      'CERRADO': 'LISTO',
      
      // Mapeo de estados en inglés para compatibilidad
      'OPEN': 'PENDIENTES',
      'PENDING': 'PENDIENTES',
      'IN_PROGRESS': 'EN_DESARROLLO',
      'IN PROGRESS': 'EN_DESARROLLO',
      'DEVELOPMENT': 'EN_DESARROLLO',
      'DEV': 'EN_DESARROLLO',
      'TESTING': 'TESTEO_LOCAL',
      'QA': 'PENDIENTE_A_QA',
      'RESOLVED': 'BUGS_CORREGIDOS',
      'COMPLETED': 'BUGS_CORREGIDOS',
      'COMPLETE': 'BUGS_CORREGIDOS',
      'DONE': 'BUGS_CORREGIDOS',
      'CLOSED': 'LISTO',
      'FINISHED': 'LISTO'
    };

    // Buscar mapeo exacto
    const mappedStatus = clickupMap[normalizedInput];
    if (mappedStatus) {
      return mappedStatus;
    }
    
    // Búsqueda parcial para casos especiales
    if (normalizedInput.includes('PENDIENTE') && normalizedInput.includes('DEV')) {
      return 'PENDIENTE_DEV';
    }
    if (normalizedInput.includes('TESTEO') && normalizedInput.includes('LOCAL')) {
      return 'TESTEO_LOCAL';
    }
    if (normalizedInput.includes('SERVIDOR') && normalizedInput.includes('DEV')) {
      return normalizedInput.includes('TESTEO') ? 'TESTEO_SERVIDOR_DEV' : 'PENDIENTE_SERVIDOR_DEV';
    }
    if (normalizedInput.includes('QA')) {
      return normalizedInput.includes('TESTEO') ? 'TESTEO_SERVIDOR_QA' : 'PENDIENTE_A_QA';
    }
    if (normalizedInput.includes('BUG')) {
      return normalizedInput.includes('CORREGIDO') ? 'BUGS_CORREGIDOS' : 'BUGS_ENCONTRADOS';
    }
    
    // Fallback por defecto
    return 'PENDIENTES';
  }

  /**
   * Obtener tiempo estimado para completar desde el estado actual
   */
  static getEstimatedCompletionTime(statusKey: string): string {
    const status = this.getStatusInfo(statusKey);
    if (!status) return 'Tiempo no disponible';

    // Estimaciones basadas en el orden del estado
    const remainingSteps = 12 - status.order;
    
    if (remainingSteps <= 0) return 'Completado';
    if (remainingSteps <= 2) return '1-2 días';
    if (remainingSteps <= 4) return '3-5 días';
    if (remainingSteps <= 6) return '1-2 semanas';
    return '2-3 semanas';
  }

  /**
   * Verificar si un estado permite edición
   */
  static canEditInStatus(statusKey: string): boolean {
    const status = this.getStatusInfo(statusKey);
    if (!status) return false;

    // Solo permitir edición en estados no finalizados
    return status.category !== 'cerrado';
  }

  /**
   * Verificar si un estado permite comentarios
   */
  static canCommentInStatus(statusKey: string): boolean {
    const status = this.getStatusInfo(statusKey);
    if (!status) return false;

    // Permitir comentarios en todos los estados excepto cerrado
    return status.category !== 'cerrado';
  }
}

/**
 * Composable para usar el servicio de estados en componentes Vue
 */
export function useTicketStatusService() {
  return {
  // Métodos principales para obtener información de estados
    getStatusData: (statusKey: string) => {
      // Primero intentar mapear estados simples en español
      const mappedStatus = TicketStatusService.mapClickUpStatus(statusKey);
      const status = TicketStatusService.getStatusInfo(mappedStatus);
      
      if (!status) {
        // Fallback para estados que no están en el mapeo
        const fallbackMap: Record<string, any> = {
          'abierto': { key: 'abierto', displayName: 'Abierto', category: 'no_iniciado', icon: '🟢', color: 'red', description: 'Ticket abierto', order: 1 },
          'en_progreso': { key: 'en_progreso', displayName: 'En Progreso', category: 'activo', icon: '🟡', color: 'yellow', description: 'Ticket en progreso', order: 5 },
          'resuelto': { key: 'resuelto', displayName: 'Resuelto', category: 'hecho', icon: '🔵', color: 'green', description: 'Ticket resuelto', order: 11 },
          'cerrado': { key: 'cerrado', displayName: 'Cerrado', category: 'cerrado', icon: '⚫', color: 'gray', description: 'Ticket cerrado', order: 12 }
        };
        
        const fallback = fallbackMap[statusKey.toLowerCase()];
        if (fallback) return fallback;
        
        return null;
      }
      
      return {
        key: status.key,
        displayName: status.displayName,
        category: status.category,
        icon: status.icon,
        color: status.color.badge.replace('bg-', '').replace('-500', ''), // Extraer solo el color
        description: status.description,
        order: status.order
      };
    },
    
    // Calcular progreso del ticket
    getStatusProgress: (statusKey: string) => {
      return TicketStatusService.calculateProgress(statusKey);
    },
    
    // Obtener todos los estados
    getAllStatuses: () => {
      return TicketStatusService.getAllStatuses();
    },
    
    // Obtener estados por categoría
    getStatusesByCategory: (category: string) => {
      return TicketStatusService.getStatusesByCategory(category);
    },
    
    // Obtener categorías
    getAllCategories: () => {
      return TicketStatusService.getAllCategories();
    },
    
    // Obtener transiciones válidas
    getValidTransitions: (currentStatus: string) => {
      return TicketStatusService.getValidTransitions(currentStatus);
    },
    
    // Formatear estado para display
    formatStatus: (statusKey: string, includeIcon: boolean = false) => {
      return TicketStatusService.formatStatusForDisplay(statusKey, includeIcon);
    },
    
    // Verificar si es un estado válido
    isValidStatus: (statusKey: string) => {
      return TicketStatusService.isValidStatus(statusKey);
    },
    
    // Obtener estadísticas
    getStatusStatistics: (tickets: any[]) => {
      return TicketStatusService.getStatusStatistics(tickets);
    },
    
    // Obtener siguiente y anterior estado
    getNextStatus: (currentStatus: string) => {
      return TicketStatusService.getNextStatus(currentStatus);
    },
    
    getPreviousStatus: (currentStatus: string) => {
      return TicketStatusService.getPreviousStatus(currentStatus);
    },
    
    // Mapear desde ClickUp
    mapClickUpStatus: (clickupStatus: string) => {
      return TicketStatusService.mapClickUpStatus(clickupStatus);
    },
    
    // Verificar permisos
    canEditInStatus: (statusKey: string) => {
      return TicketStatusService.canEditInStatus(statusKey);
    },
    
    canCommentInStatus: (statusKey: string) => {
      return TicketStatusService.canCommentInStatus(statusKey);
    },
    
    // Tiempo estimado
    getEstimatedCompletionTime: (statusKey: string) => {
      return TicketStatusService.getEstimatedCompletionTime(statusKey);
    }
  };
}

export default TicketStatusService;
